import { supabase } from '../../lib/supabase'
import type { SheetEntry } from './sheetFigures'
import { getRequestedLeave } from './leaveReportStore'

type EntryRow = {
  employee_id: string
  team_id: string
  start_time: string | null
  end_time: string | null
  assignment_id: string | null
  employees: { display_name: string; employee_identifier: string } | null
  status_codes: { category: string } | null
  assignments: { name: string; sort_priority: number; activities: { name: string } | null } | null
}

/**
 * Every schedule entry on one date that the signed-in account can read;
 * row-level security limits a supervisor to their own teams.
 */
export async function getSheetEntries(date: string): Promise<SheetEntry[]> {
  const { data, error } = await supabase
    .from('schedule_entries')
    .select(
      'employee_id, team_id, start_time, end_time, assignment_id, employees(display_name, employee_identifier), status_codes(category), assignments(name, sort_priority, activities(name))',
    )
    .eq('date', date)
  if (error) throw new Error('The assignment sheet could not be loaded.')
  return ((data ?? []) as unknown as EntryRow[]).flatMap((row) =>
    row.employees
      ? [
          {
            employeeId: row.employee_id,
            identifier: row.employees.employee_identifier,
            employee: row.employees.display_name,
            teamId: row.team_id,
            statusCategory: row.status_codes?.category ?? '',
            startTime: row.start_time,
            endTime: row.end_time,
            assignmentId: row.assignment_id,
            assignment: row.assignments?.name ?? null,
            sortPriority: row.assignments?.sort_priority ?? null,
            activity: row.assignments?.activities?.name ?? null,
          },
        ]
      : [],
  )
}

// Employees whose full-day leave was approved for the date.
export async function getApprovedLeaveOn(date: string): Promise<Set<string>> {
  const requests = await getRequestedLeave(date, date)
  return new Set(requests.filter((request) => request.state === 'approved' && request.fullDay !== false).map((request) => request.employeeId))
}
